/**
 * /reset/request — request a password-reset magic-link by email.
 *
 * The API returns 204 regardless of whether the email is
 * registered, so the UI displays the same confirmation page in
 * both cases. The page invites the user to check their email.
 */

import { type FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { authApi, type AuthApiError } from "../../auth/api";

export function PasswordResetRequest() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await authApi.passwordResetRequest(email);
      setSubmitted(true);
    } catch (exc) {
      const apiError = exc as AuthApiError;
      setError(apiError.message ?? "Could not send reset email.");
    } finally {
      setBusy(false);
    }
  }

  if (submitted) {
    return (
      <div className="auth-shell">
        <div className="auth-card">
          <h1 className="auth-title">Check your email</h1>
          <p className="auth-subtle">
            If an account exists for that email, we have sent a
            password-reset link. The link expires in 15 minutes.
          </p>
          <div className="auth-links">
            <Link to="/login">Back to sign in</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-shell">
      <form className="auth-card" onSubmit={onSubmit} noValidate>
        <h1 className="auth-title">Reset your password</h1>
        <p className="auth-subtle">
          Enter the email associated with your account and we will
          send you a link to choose a new password.
        </p>
        <label className="auth-field">
          <span className="auth-label">Email</span>
          <input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        {error !== null && (
          <div className="auth-error" role="alert">
            {error}
          </div>
        )}
        <button type="submit" className="auth-primary" disabled={busy}>
          {busy ? "Sending..." : "Send reset link"}
        </button>
        <div className="auth-links">
          <Link to="/login">Back to sign in</Link>
        </div>
      </form>
    </div>
  );
}
